import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import api from '../../lib/api'
import { useImpersonation } from '@/hooks/useImpersonation'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/use-toast'
import {
  ArrowLeft,
  Users,
  MessageSquare,
  Phone,
  DollarSign,
  Bot,
  User,
  Eye,
  Pencil,
  MoreHorizontal,
  UserCheck,
  UserX,
  Save,
  X,
  Globe,
  Palette,
  Plug,
  Mic,
  CheckCircle2,
  XCircle,
  ExternalLink
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import CostTrendChart from '@/components/admin/CostTrendChart'
import CostBreakdownChart from '@/components/admin/CostBreakdownChart'
import VoiceEngineSection from '@/components/admin/VoiceEngineSection'
import PromptsSection from '@/components/admin/PromptsSection'
import AssistantsNumbersSection from '@/components/admin/AssistantsNumbersSection'
import BillingOverrideSection from '@/components/admin/BillingOverrideSection'
import OutboundWebhooksSection from '@/components/admin/OutboundWebhooksSection'
import { TierBadge } from '@/components/ui/TierBadge'

interface OrgDetail {
  id: string
  name: string
  slug: string
  status: string
  subscriptionTier: string
  subscriptionStatus: string
  trialEndsAt: string | null
  createdAt: string
  settings: any
  branding: any
  integrations: any
  widgetKey: string | null
  limits: any
  stats: {
    agents: number
    voiceAssistants: number
    conversations: {
      total: number
      active: number
      last30Days: number
    }
    calls: {
      total: number
      last30Days: number
      totalMinutes: number
      totalCost: number
      costThisMonth: number
    }
    costs: {
      voiceCostThisMonth: number
      voiceCostLastMonth: number
      phoneMonthlyCost: number
    }
    currentMonthUsage: Record<string, number>
  }
  agents: Array<{
    id: string
    name: string
    email: string
    role: string
    status: string
    createdAt: string
    lastLoginAt: string | null
    lastActive: string | null
    isSuperAdmin?: boolean
    isPlatformAdmin?: boolean
  }>
}

interface OrgCosts {
  dailyCosts: Array<{
    date: string
    calls: number
    cost: number
    minutes: number
  }>
  costBreakdown: {
    transport: number
    stt: number
    llm: number
    tts: number
  }
  phoneMonthlyCost: number
  projectedMonthlyCost: number
}

// OrgVoiceConfig and PromptData interfaces moved to their respective sub-components

export default function OrganizationDetailPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const { startImpersonation } = useImpersonation()
  const { agent } = useAuth()
  const isSuperAdmin = (agent as any)?.isSuperAdmin
  const { toast } = useToast()
  const [org, setOrg] = useState<OrgDetail | null>(null)
  const [costs, setCosts] = useState<OrgCosts | null>(null)
  // voiceConfig state removed — now in AssistantsNumbersSection
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    name: '',
    subscriptionTier: '',
    monthly_chats: 0,
    monthly_voice_minutes: 0,
    voiceEnabled: false,
    aiEnabled: false,
    trialEndsAt: ''
  })
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (orgId) {
      loadOrgDetail()
    }
  }, [orgId])

  useEffect(() => {
    if (orgId && activeTab === 'costs' && !costs) {
      loadCosts()
    }
  }, [activeTab, orgId])

  const [loadError, setLoadError] = useState<string | null>(null)

  const loadOrgDetail = async () => {
    setLoadError(null)
    try {
      const response = await api.get(`/api/platform/organizations/${orgId}`)
      if (response.data.success) {
        setOrg(response.data.organization)
      }
    } catch (error: any) {
      const status = error?.response?.status
      const msg = error?.response?.data?.message || error?.message || 'Unknown error'
      console.error('Failed to load organization:', status, msg)
      if (status === 403) {
        setLoadError('Access denied. Platform admin privileges required.')
      } else if (status === 404) {
        setLoadError('Organization not found in the database.')
      } else {
        setLoadError(`Failed to load organization: ${msg}`)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const loadCosts = async () => {
    try {
      const response = await api.get(`/api/platform/organizations/${orgId}/costs`)
      if (response.data.success) {
        setCosts(response.data)
      }
    } catch (error) {
      console.error('Failed to load costs:', error)
    }
  }

  const handleViewAsOrg = () => {
    if (org) {
      startImpersonation(org.id, org.name, org.slug, org.subscriptionTier)
      navigate('/')
    }
  }

  const startEditing = () => {
    if (!org) return
    setEditForm({
      name: org.name,
      subscriptionTier: org.subscriptionTier,
      monthly_chats: org.limits?.monthly_chats || 0,
      monthly_voice_minutes: org.limits?.monthly_voice_minutes || 0,
      voiceEnabled: org.settings?.voiceEnabled || false,
      aiEnabled: org.settings?.aiEnabled !== false,
      trialEndsAt: org.trialEndsAt ? org.trialEndsAt.split('T')[0] : ''
    })
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setIsEditing(false)
  }

  const saveOrgSettings = async () => {
    if (!org) return
    setIsSaving(true)
    try {
      const payload: any = {
        name: editForm.name,
        subscriptionTier: editForm.subscriptionTier,
        limits: {
          monthly_chats: editForm.monthly_chats,
          monthly_voice_minutes: editForm.monthly_voice_minutes
        },
        settings: {
          voiceEnabled: editForm.voiceEnabled,
          aiEnabled: editForm.aiEnabled
        }
      }
      if (editForm.trialEndsAt) {
        payload.trialEndsAt = new Date(editForm.trialEndsAt).toISOString()
      }
      const response = await api.put(`/api/platform/organizations/${orgId}`, payload)
      if (response.data.success) {
        toast({ title: 'Organization updated' })
        setIsEditing(false)
        loadOrgDetail()
      }
    } catch (error: any) {
      toast({ title: 'Failed to update', description: error.response?.data?.message || 'Unknown error', variant: 'destructive' })
    } finally {
      setIsSaving(false)
    }
  }

  const toggleUserStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active'
    try {
      const response = await api.put(`/api/platform/organizations/${orgId}/users/${userId}/status`, { status: newStatus })
      if (response.data.success) {
        toast({ title: `User ${newStatus === 'active' ? 'activated' : 'suspended'}` })
        loadOrgDetail()
      }
    } catch (error: any) {
      toast({ title: 'Failed to update user', description: error.response?.data?.message || 'Unknown error', variant: 'destructive' })
    }
  }

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'default',
      online: 'default',
      pending: 'secondary',
      offline: 'secondary',
      suspended: 'destructive'
    }
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    )
  }

  if (!org) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{loadError || 'Organization not found'}</p>
        <div className="flex items-center justify-center gap-3 mt-4">
          <Button variant="outline" onClick={() => { setIsLoading(true); loadOrgDetail() }}>
            Retry
          </Button>
          <Link to="/admin/organizations">
            <Button variant="outline">Back to Organizations</Button>
          </Link>
        </div>
      </div>
    )
  }

  const totalCostThisMonth = (org.stats.costs.voiceCostThisMonth + org.stats.costs.phoneMonthlyCost)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin/organizations">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">{org.name}</h1>
            {getStatusBadge(org.status)}
            <TierBadge tier={org.subscriptionTier} />
            {/* Quick Health Dots */}
            <div className="flex items-center gap-1.5 ml-2" title="Voice | Chats | Assistants">
              <span className={`h-2.5 w-2.5 rounded-full ${org.settings?.voiceEnabled ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
              <span className={`h-2.5 w-2.5 rounded-full ${org.stats.conversations.total > 0 ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
              <span className={`h-2.5 w-2.5 rounded-full ${org.stats.voiceAssistants > 0 ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
            </div>
          </div>
          <p className="text-muted-foreground mt-1">
            {org.slug}
            {org.settings?.website && (
              <a href={org.settings.website.startsWith('http') ? org.settings.website : `https://${org.settings.website}`}
                target="_blank" rel="noopener noreferrer"
                className="ml-3 text-brand-primary hover:underline inline-flex items-center gap-1 text-sm">
                <Globe className="h-3 w-3" />{org.settings.website}
              </a>
            )}
          </p>
        </div>
        <Button onClick={handleViewAsOrg} variant="outline" className="gap-2">
          <Eye className="h-4 w-4" />
          View as this org
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full overflow-x-auto justify-start">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="config">Config</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="voice-prompts">Voice & Prompts</TabsTrigger>
          <TabsTrigger value="costs">Costs</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="billing">Billing</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Agents</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{org.stats.agents}</div>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Conversations</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{org.stats.conversations.total}</div>
                <p className="text-xs text-muted-foreground">
                  {org.stats.conversations.last30Days} last 30d
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Voice Calls</CardTitle>
                <Phone className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{org.stats.calls.total}</div>
                <p className="text-xs text-muted-foreground">
                  {org.stats.calls.totalMinutes} min total
                </p>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">AI Assistants</CardTitle>
                <Bot className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{org.stats.voiceAssistants}</div>
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Cost/Mo</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(totalCostThisMonth)}</div>
                <p className="text-xs text-muted-foreground">
                  voice + phone
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Org Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="card-inset">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Organization Info</CardTitle>
                {!isEditing ? (
                  <Button variant="ghost" size="sm" onClick={startEditing} className="gap-1">
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={isSaving}>
                      <X className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                    <Button size="sm" onClick={saveOrgSettings} disabled={isSaving} className="gap-1">
                      <Save className="h-3 w-3" /> {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Subscription Tier</Label>
                      <Select value={editForm.subscriptionTier} onValueChange={v => setEditForm(f => ({ ...f, subscriptionTier: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="trial">Trial</SelectItem>
                          <SelectItem value="associate">Associate</SelectItem>
                          <SelectItem value="partner">Partner</SelectItem>
                          <SelectItem value="principal">Principal</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Monthly Chat Limit</Label>
                        <Input type="number" value={editForm.monthly_chats} onChange={e => setEditForm(f => ({ ...f, monthly_chats: parseInt(e.target.value) || 0 }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>Monthly Voice Min Limit</Label>
                        <Input type="number" value={editForm.monthly_voice_minutes} onChange={e => setEditForm(f => ({ ...f, monthly_voice_minutes: parseInt(e.target.value) || 0 }))} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Voice Enabled</Label>
                      <Switch checked={editForm.voiceEnabled} onCheckedChange={v => setEditForm(f => ({ ...f, voiceEnabled: v }))} />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>AI Enabled</Label>
                      <Switch checked={editForm.aiEnabled} onCheckedChange={v => setEditForm(f => ({ ...f, aiEnabled: v }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Trial End Date</Label>
                      <Input type="date" value={editForm.trialEndsAt} onChange={e => setEditForm(f => ({ ...f, trialEndsAt: e.target.value }))} />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Created</span>
                      <span>{new Date(org.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subscription</span>
                      <span className="capitalize">{org.subscriptionTier} ({org.subscriptionStatus})</span>
                    </div>
                    {org.trialEndsAt && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Trial Ends</span>
                        <span>{new Date(org.trialEndsAt).toLocaleDateString()}</span>
                      </div>
                    )}
                    {org.limits && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Monthly Chat Limit</span>
                          <span>{org.limits.monthly_chats?.toLocaleString() || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Monthly Voice Min Limit</span>
                          <span>{org.limits.monthly_voice_minutes?.toLocaleString() || 'N/A'}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Voice Enabled</span>
                      <span>{org.settings?.voiceEnabled ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">AI Enabled</span>
                      <span>{org.settings?.aiEnabled !== false ? 'Yes' : 'No'}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className="card-inset">
              <CardHeader>
                <CardTitle>Cost Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Voice Cost (This Month)</span>
                  <span className="font-mono">{formatCurrency(org.stats.costs.voiceCostThisMonth)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Voice Cost (Last Month)</span>
                  <span className="font-mono">{formatCurrency(org.stats.costs.voiceCostLastMonth)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone Cost (Monthly)</span>
                  <span className="font-mono">{formatCurrency(org.stats.costs.phoneMonthlyCost)}</span>
                </div>
                <div className="border-t pt-3 flex justify-between font-medium">
                  <span>Total This Month</span>
                  <span className="font-mono">{formatCurrency(totalCostThisMonth)}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Voice AI Configuration */}
          {org.settings?.voiceConfig && (
            <Card className="card-inset">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Mic className="h-4 w-4" /> Voice AI Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                  {org.settings.voiceConfig.agentName && (
                    <div><span className="text-muted-foreground">Agent Name</span><p className="font-medium">{org.settings.voiceConfig.agentName}</p></div>
                  )}
                  {org.settings.voiceConfig.greetingName && (
                    <div><span className="text-muted-foreground">Greeting Name</span><p className="font-medium">{org.settings.voiceConfig.greetingName}</p></div>
                  )}
                  {org.settings.voiceConfig.voiceId && (
                    <div><span className="text-muted-foreground">Voice</span><p className="font-medium">{org.settings.voiceConfig.voiceId}</p></div>
                  )}
                  {org.settings.voiceConfig.tonePreset && (
                    <div><span className="text-muted-foreground">Tone</span><p className="font-medium capitalize">{org.settings.voiceConfig.tonePreset}</p></div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Wizard</span>
                    <p className="font-medium">{org.settings.voiceConfig.wizardCompleted ? 'Completed' : 'Not completed'}</p>
                  </div>
                  {org.settings.voiceConfig.practiceAreas?.length > 0 && (
                    <div className="col-span-2 md:col-span-3">
                      <span className="text-muted-foreground">Practice Areas</span>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {org.settings.voiceConfig.practiceAreas.map((area: string, i: number) => (
                          <Badge key={i} variant="secondary" className="text-xs">{area}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Config Tab */}
        <TabsContent value="config" className="space-y-6">
          {/* Widget Configuration */}
          <Card className="card-inset">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Globe className="h-4 w-4" /> Widget & Chat Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Assistant Name</span>
                  <p className="font-medium">{org.settings?.assistantName || 'Not set'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Welcome Message</span>
                  <p className="font-medium">{org.settings?.welcomeMessage || 'Not set'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Widget Position</span>
                  <p className="font-medium capitalize">{org.settings?.position || 'bottom-right'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Booking URL</span>
                  <p className="font-medium">
                    {org.settings?.bookingUrl
                      ? <a href={org.settings.bookingUrl} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline inline-flex items-center gap-1">{org.settings.bookingUrl} <ExternalLink className="h-3 w-3" /></a>
                      : 'Not set'}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Proactive Messaging</span>
                  <p className="font-medium">
                    {org.settings?.proactiveEnabled
                      ? `Enabled (${org.settings.proactiveDelay || 0}s delay)`
                      : 'Disabled'}
                  </p>
                </div>
                {org.settings?.conversationStarters?.length > 0 && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Conversation Starters</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {org.settings.conversationStarters.map((starter: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">{starter}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {org.widgetKey && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Widget Key</span>
                    <p className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1 inline-block">{org.widgetKey}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Branding */}
          <Card className="card-inset">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Palette className="h-4 w-4" /> Branding</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Company Name</span>
                  <p className="font-medium">{org.branding?.companyName || org.name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Theme</span>
                  <p className="font-medium capitalize">{org.branding?.theme || 'light'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Primary Color</span>
                  {org.branding?.primaryColor ? (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="h-6 w-6 rounded border" style={{ backgroundColor: org.branding.primaryColor }} />
                      <span className="font-mono text-xs">{org.branding.primaryColor}</span>
                    </div>
                  ) : (
                    <p className="font-medium">Default</p>
                  )}
                </div>
                {org.branding?.logoUrl && (
                  <div>
                    <span className="text-muted-foreground">Logo</span>
                    <img src={org.branding.logoUrl} alt="Logo" className="h-10 mt-1 rounded" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Integrations */}
          <Card className="card-inset">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Plug className="h-4 w-4" /> Integrations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Voice</span>
                  <p className="font-medium flex items-center gap-1">
                    {org.settings?.voiceEnabled
                      ? <><CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> Enabled</>
                      : <><XCircle className="h-3.5 w-3.5 text-muted-foreground/50" /> Disabled</>}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">AI</span>
                  <p className="font-medium flex items-center gap-1">
                    {org.settings?.aiEnabled !== false
                      ? <><CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> Enabled</>
                      : <><XCircle className="h-3.5 w-3.5 text-muted-foreground/50" /> Disabled</>}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">n8n Webhook</span>
                  <p className="font-medium">
                    {org.integrations?.n8nWebhookUrl
                      ? <span className="text-green-600">Configured</span>
                      : 'Not configured'}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">CRM</span>
                  <p className="font-medium">
                    {org.integrations?.crm
                      ? <span className="text-green-600">{typeof org.integrations.crm === 'string' ? org.integrations.crm : 'Configured'}</span>
                      : 'Not configured'}
                  </p>
                </div>
                {org.settings?.industry && (
                  <div>
                    <span className="text-muted-foreground">Industry</span>
                    <p className="font-medium capitalize">{org.settings.industry}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card className="card-inset">
            <CardHeader>
              <CardTitle>Team Members ({org.agents.length})</CardTitle>
              <CardDescription>All users in this organization</CardDescription>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Last Active</TableHead>
                    <TableHead className="hidden md:table-cell">Joined</TableHead>
                    <TableHead className="w-[70px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {org.agents.map((agent) => (
                    <TableRow key={agent.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                            <User className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <span className="font-medium block truncate">{agent.name}</span>
                            <span className="text-xs text-muted-foreground block truncate sm:hidden">{agent.email}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground hidden sm:table-cell">{agent.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant={agent.role === 'admin' ? 'default' : agent.role === 'supervisor' ? 'secondary' : 'outline'}>
                            {agent.role === 'admin' ? 'Admin' : agent.role === 'supervisor' ? 'Supervisor' : agent.role === 'agent' ? 'Team Member' : agent.role}
                          </Badge>
                          {agent.isSuperAdmin && (
                            <Badge className="border-transparent bg-[#ede9fe] dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 shadow-sm text-[10px] whitespace-nowrap">
                              Super Admin
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(agent.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground hidden md:table-cell">
                        {agent.lastActive
                          ? new Date(agent.lastActive).toLocaleString()
                          : agent.lastLoginAt
                            ? new Date(agent.lastLoginAt).toLocaleString()
                            : 'Never'}
                      </TableCell>
                      <TableCell className="text-sm hidden md:table-cell">
                        {new Date(agent.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {agent.status === 'active' || agent.status === 'online' ? (
                              <DropdownMenuItem onClick={() => toggleUserStatus(agent.id, 'active')} className="text-destructive">
                                <UserX className="h-4 w-4 mr-2" /> Suspend
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => toggleUserStatus(agent.id, 'suspended')}>
                                <UserCheck className="h-4 w-4 mr-2" /> Activate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  {org.agents.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voice & Prompts Tab */}
        <TabsContent value="voice-prompts" className="space-y-4">
          <VoiceEngineSection orgId={orgId!} />
          {isSuperAdmin && <PromptsSection orgId={orgId!} />}
          <AssistantsNumbersSection orgId={orgId!} />
        </TabsContent>

        {/* Costs Tab */}
        <TabsContent value="costs" className="space-y-6">
          {costs ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CostTrendChart data={costs.dailyCosts} />
                <CostBreakdownChart data={costs.costBreakdown} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="card-inset">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Phone Cost</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(costs.phoneMonthlyCost)}</div>
                    <p className="text-xs text-muted-foreground">monthly recurring</p>
                  </CardContent>
                </Card>
                <Card className="card-inset">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Voice Cost (This Month)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(org.stats.costs.voiceCostThisMonth)}</div>
                    <p className="text-xs text-muted-foreground">
                      {org.stats.calls.last30Days} calls last 30d
                    </p>
                  </CardContent>
                </Card>
                <Card className="card-inset">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Projected Monthly</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(costs.projectedMonthlyCost)}</div>
                    <p className="text-xs text-muted-foreground">based on 7-day avg</p>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">Loading cost data...</div>
          )}
        </TabsContent>

        {/* Webhooks Tab — outbound integrations (Make.com, n8n, custom CRMs) */}
        <TabsContent value="webhooks" className="space-y-4">
          <OutboundWebhooksSection orgId={orgId!} />
        </TabsContent>

        {/* Billing Tab — super-admin only */}
        {isSuperAdmin && (
          <TabsContent value="billing" className="space-y-4">
            <BillingOverrideSection orgId={orgId!} onOrgChanged={loadOrgDetail} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
